import { postRequest, getRequest } from "./apiService";
import { API_BASE_URL } from "../config/config";

/**
 * Posts selected CMIs to the classification API for a given application and customer.
 *
 * @param {Object} params - Contains all required inputs.
 * @param {string} params.applicationId - The ID of the application.
 * @param {string} params.customerId - The ID of the customer.
 * @param {string[]} params.cmiList - Array of selected CMI codes.
 * @param {string} params.accessToken - The JWT access token.
 * @param {string} params.refreshToken - The JWT refresh token.
 * @returns {Promise<Object>} - The response from the API.
 */
export async function postClassificationForApplication({
  applicationId,
  customerId,
  cmiList,
  accessToken,
  refreshToken
}) {
  const url = `${API_BASE_URL}/insureai/ml/intake/applications/v2/add_classification?application_id=${encodeURIComponent(
    applicationId
  )}&customer_id=${encodeURIComponent(customerId)}`;

  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
    "X-Access-Token": accessToken,
    "X-Refresh-Token": refreshToken
  };

  try {
    const response = await postRequest(url, cmiList, headers);
    return response;
  } catch (error) {
    console.error("Error posting classification for application:", error);
    throw error;
  }
}

/**
 * Fetches NAICS-CMI-ISO details based on a search type and code.
 *
 * @param {Object} params - Parameters for the API request.
 * @param {string} params.search_type - Type of search: 'cmi', 'naics', or 'iso-gl'.
 * @param {string} params.search_value - Value to search by (e.g., CMI code like "2103").
 * @param {string} accessToken - The JWT access token.
 * @param {string} refreshToken - The JWT refresh token.
 * @returns {Promise<Object>} - The response data from the API.
 */
export async function getNaicsCmiIsoDetails(
  { search_type, search_value },
  accessToken,
  refreshToken
) {
  if (!search_type || !search_value) {
    throw new Error("Both search_type and search_value are required.");
  }

  const url = `${API_BASE_URL}/insureai/ml/intake/applications/v2/get_naics_cmi_iso_details?search_type=${encodeURIComponent(
    search_type
  )}&search_value=${encodeURIComponent(search_value)}`;

  const headers = {
    accept: "application/json",
    "X-Access-Token": accessToken,
    "X-Refresh-Token": refreshToken
  };

  try {
    const response = await getRequest(url, headers);
    return response;
  } catch (error) {
    console.error("Error fetching NAICS-CMI-ISO details:", error);
    throw error;
  }
}
